import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { submitClearance } from "../api/clearance";
import { classify, ClassSuggestion } from "../api/classify";
import { useAuth } from "../auth/AuthContext";

export default function RandesSearch() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [brandName, setBrandName] = useState("");
  const [description, setDescription] = useState("");
  const [niceClass, setNiceClass] = useState<number | "">("");
  const [suggestions, setSuggestions] = useState<ClassSuggestion[] | null>(null);
  const [notes, setNotes] = useState<string | null>(null);

  const [classifying, setClassifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confStyle: Record<string, string> = {
    high: "bg-white text-maestro-900 border-maestro-900",
    medium: "bg-white text-maestro-700 border-maestro-300",
    low: "bg-maestro-50 text-maestro-400 border-maestro-200",
  };

  async function runClassify(): Promise<ClassSuggestion[] | null> {
    if (!description.trim()) {
      setError("Describe the goods/services so we can pick the right Nice class.");
      return null;
    }
    setClassifying(true);
    setError(null);
    try {
      const resp = await classify(description);
      setSuggestions(resp.classes);
      setNotes(resp.notes || null);
      return resp.classes;
    } catch (err: any) {
      const pieces: string[] = [];
      if (err?.response?.status) pieces.push(`HTTP ${err.response.status}`);
      if (err?.response?.data?.error) pieces.push(err.response.data.error);
      if (err?.code) pieces.push(err.code);
      setError(pieces.join(" · ") || "Classification failed. Enter a class manually.");
      return null;
    } finally {
      setClassifying(false);
    }
  }

  async function onSuggestClick() {
    await runClassify();
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) {
      setError("Session expired — please log in again.");
      return;
    }
    setError(null);

    // If user hasn't picked a class yet, auto-classify from description and
    // pick the highest-confidence suggestion.
    let cls: number | "" = niceClass;
    if (cls === "") {
      const classes = await runClassify();
      if (!classes || classes.length === 0) {
        setError("Could not determine a Nice class. Enter one manually.");
        return;
      }
      cls = classes[0].class;
      setNiceClass(cls);
    }

    setSubmitting(true);
    try {
      const result = await submitClearance(brandName, cls as number, token);
      navigate(`/reports/${result.report_id}`);
    } catch (err: any) {
      const pieces: string[] = [];
      if (err?.response?.status) pieces.push(`HTTP ${err.response.status}`);
      if (err?.response?.data?.error) pieces.push(err.response.data.error);
      if (err?.response?.data?.detail) pieces.push(err.response.data.detail);
      if (err?.code) pieces.push(err.code);
      if (err?.message && pieces.length === 0) pieces.push(err.message);
      setError(pieces.join(" · ") || "Could not submit clearance.");
      console.error("clearance submit error", err);
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-2xl sm:text-3xl text-maestro-900 mb-1">
        New brand clearance
      </h1>
      <p className="text-maestro-700 mb-8 text-sm">
        Describe the goods or services, give the brand name, and run.
        The Nice class is picked from your description — override it if
        you want.
      </p>

      <form onSubmit={onSubmit} className="bg-white border border-maestro-200 p-5 sm:p-6 space-y-5">
        {/* Brand name */}
        <label className="block">
          <span className="text-xs uppercase tracking-[0.1em] text-maestro-400">Brand name</span>
          <input
            type="text"
            value={brandName}
            onChange={(e) => setBrandName(e.target.value)}
            required
            placeholder="e.g. Cheap Tuesday"
            className="mt-1 block w-full border border-maestro-200 bg-white px-3 py-2 text-sm text-maestro-900 focus:border-maestro-900 focus:outline-none"
          />
        </label>

        {/* Goods/services description */}
        <div>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.1em] text-maestro-400">
              Goods / services
            </span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder='e.g. "streetwear T-shirts and hoodies sold online and in a concept store"'
              className="mt-1 block w-full border border-maestro-200 bg-white px-3 py-2 text-sm text-maestro-900 focus:border-maestro-900 focus:outline-none resize-y"
            />
          </label>
          <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-maestro-400">
              The Nice class is inferred from this — you can override below.
            </span>
            <button
              type="button"
              onClick={onSuggestClick}
              disabled={classifying || !description.trim()}
              className="text-xs font-display uppercase tracking-wider text-maestro-900 border border-maestro-900 hover:bg-maestro-900 hover:text-white transition px-3 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {classifying ? "Thinking…" : "Suggest Nice class"}
            </button>
          </div>
        </div>

        {/* Suggestions (appear inline after classify) */}
        {suggestions && suggestions.length > 0 && (
          <div>
            <span className="text-xs uppercase tracking-[0.1em] text-maestro-400 block mb-2">
              Suggested classes
            </span>
            <ul className="space-y-2">
              {suggestions.map((s) => (
                <li
                  key={s.class}
                  className={`border p-3 cursor-pointer transition ${
                    niceClass === s.class
                      ? "border-maestro-900"
                      : "border-maestro-200 hover:border-maestro-900"
                  }`}
                  onClick={() => setNiceClass(s.class)}
                >
                  <div className="flex items-start gap-3">
                    <span className="font-display text-base text-maestro-900 min-w-[3rem]">
                      Cl. {s.class}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="font-display font-semibold text-sm text-maestro-900">
                          {s.heading}
                        </span>
                        <span
                          className={`text-[0.6rem] px-2 py-0.5 uppercase tracking-wider border ${
                            confStyle[s.confidence] || ""
                          }`}
                        >
                          {s.confidence}
                        </span>
                      </div>
                      <p className="text-xs text-maestro-700">{s.reasoning}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {notes && (
              <p className="mt-2 text-xs text-maestro-400 italic border-l-2 border-maestro-200 pl-3">
                {notes}
              </p>
            )}
          </div>
        )}

        {/* Manual override */}
        <label className="block">
          <span className="text-xs uppercase tracking-[0.1em] text-maestro-400">
            Nice class {niceClass === "" ? "(will auto-pick on run)" : ""}
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={45}
            value={niceClass}
            onChange={(e) => {
              const v = e.target.value;
              setNiceClass(v === "" ? "" : parseInt(v));
            }}
            placeholder="Auto"
            className="mt-1 block w-32 border border-maestro-200 bg-white px-3 py-2 text-sm text-maestro-900 focus:border-maestro-900 focus:outline-none"
          />
        </label>

        {error && <p className="text-red-600 text-sm">{error}</p>}

        <button
          type="submit"
          disabled={submitting || classifying}
          className="w-full sm:w-auto bg-maestro-900 hover:bg-black disabled:opacity-60 text-white px-6 py-2 font-display text-sm tracking-wider"
        >
          {submitting ? "Starting…" : classifying ? "Classifying…" : "Run clearance"}
        </button>
      </form>
    </div>
  );
}
